import React, { useMemo } from 'react';
import { Controller, useWatch } from 'react-hook-form-v7';
import type { UseFormReturn } from 'react-hook-form-v7';
import { Label } from '@parrot-software/design-system';
import { useTranslation } from 'react-i18next';
import { cn } from '@parrot-software/utils';

import {
  useEmailVerificationBadge,
  useEmployeeFormContext,
  usePhoneVerificationBadge,
} from '@personnel/presentation/components/form/EmployeeForm/hooks';
import {
  styles,
  EMPLOYEE_FORM_IDS,
  DisabledSectionHelp,
} from '@personnel/presentation/components/form/EmployeeForm/shared';
import {
  PERMISSIONS_THAT_REQUIRE_CREDENTIALS,
  getFrontendIdsForBackendIds,
} from '@personnel/presentation/components/form/EmployeeForm/utils';
import { FormTextField, VerificationBadge } from '@personnel/presentation/components/shared';
import type { EmployeeFormData } from '@personnel/presentation/types';

interface ContactDataSectionProps {
  form: UseFormReturn<EmployeeFormData>;
}

/**
 * The values the account is reached at: the email, which is also how the person signs in, and the
 * phone number.
 *
 * The phone number is text and not a disabled input. A disabled input would say "this could be
 * edited, just not now"; the truth is that it is never edited from here, only from its owner's own
 * profile. It shows even when there is none, so its absence is visible instead of silent, and only
 * a number that exists carries a badge.
 *
 * @param {ContactDataSectionProps} props - Component properties
 */
export const ContactDataSection = ({ form }: ContactDataSectionProps): React.ReactElement => {
  const { t } = useTranslation('personnel-employees');
  const { t: tRoles } = useTranslation('roles');
  const { control } = useEmployeeFormContext(form);

  const credentialsEnabled = useWatch({ control, name: '_credentialsEnabled' });
  const isEditMode = useWatch({ control, name: '_isEditMode' });
  const phoneNumber = useWatch({ control, name: 'phoneNumber' });
  const verificationStatus = useEmailVerificationBadge(control);
  const phoneVerificationStatus = usePhoneVerificationBadge(control);

  const isDisabled = !credentialsEnabled;

  const requiredPermissionNames = useMemo(() => {
    const frontendIds = getFrontendIdsForBackendIds(PERMISSIONS_THAT_REQUIRE_CREDENTIALS);
    return frontendIds.map((frontendId) => tRoles(`permissions.${frontendId}.name`)).join(', ');
  }, [tRoles]);

  return (
    <div>
      <div className={styles.containerCards}>
        <div>
          <h2 className={cn(styles.header.cardTitle, { 'text-muted-foreground': isDisabled })}>
            {t('form.contactSection.title')}
          </h2>
          <p className={cn(styles.header.description, { 'text-muted-foreground': isDisabled })}>
            {t('form.contactSection.description')}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          <div className={styles.gridGroup}>
            {/*
              The badge sits beside the input, so editing borrows the next cell for it. The input
              is capped at one cell so it keeps the width it has when there is no badge.
            */}
            <div className={cn({ 'sm:col-span-2': isEditMode })}>
              <Controller
                control={control}
                name="email"
                render={({ field, fieldState }) => (
                  <FormTextField
                    className={cn({ 'sm:max-w-[calc(50%-0.5rem)]': isEditMode })}
                    data-testid="email-input"
                    disabled={isDisabled}
                    error={fieldState.error?.message}
                    field={field}
                    id={EMPLOYEE_FORM_IDS.EMAIL}
                    inputSuffix={<VerificationBadge verificationStatus={verificationStatus} />}
                    label={t('form.contactSection.emailLabel')}
                    maxLength={500}
                    placeholder={t('form.contactSection.emailPlaceholder')}
                    required={!isDisabled}
                    type="email"
                  />
                )}
              />
            </div>
          </div>

          <p className={styles.header.description}>
            {t(isEditMode ? 'form.contactSection.emailNoteEdit' : 'form.contactSection.emailNoteCreate')}
          </p>

          {isEditMode && (
            <div className={styles.gridGroup}>
              {/*
                One badge, in one place in the markup. From `sm` up it sits to the right of the
                number; on a phone it wraps up next to the label, where the line is too narrow to
                hold both. The note always closes the field full width.
              */}
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 sm:col-span-2">
                <Label className="order-1 sm:w-full">{t('form.contactSection.phoneLabel')}</Label>
                <VerificationBadge
                  className="order-2 shrink-0 sm:order-3"
                  verificationStatus={phoneVerificationStatus}
                />
                <p className="order-3 w-full text-sm text-foreground sm:order-2 sm:w-auto" data-testid="phone-readonly">
                  {phoneNumber || '-'}
                </p>
                <p className={cn(styles.header.description, 'order-4 w-full')}>{t('form.contactSection.phoneNote')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {isDisabled && (
        <DisabledSectionHelp
          i18nKey="form.contactSection.credentialsDisabledHelp"
          values={{ permissions: requiredPermissionNames }}
        />
      )}
    </div>
  );
};
